import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import logoLight from '../assets/logo/icon-text-logo-light.svg';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  const resetMutation = useMutation({
    mutationFn: async (payload: { token: string; password: string }) => {
      const res = await api.post('/api/v1/auth/reset-password', {
        token: payload.token,
        password: payload.password,
      });
      if (!res.success) {
        throw new Error(res.error ?? 'Password reset failed');
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (password.length < 8) {
      setValidationError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setValidationError('Passwords do not match.');
      return;
    }

    if (!token) return;

    resetMutation.mutate({ token, password });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="flex justify-center mb-8">
          <img src={logoLight} alt="Halo" className="h-8" />
        </div>

        <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl border border-gray-800 p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-bold text-white">Set New Password</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Choose a new password for your account</p>

          {!token ? (
            <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
              Invalid or missing reset link.
            </div>
          ) : resetMutation.isSuccess ? (
            <>
              <div className="px-3 py-2 mb-4 text-sm text-green-400 bg-green-950/50 border border-green-900 rounded-lg">
                Password has been reset. You can now sign in with your new password.
              </div>
              <Link
                to="/login"
                className="block w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors text-center"
              >
                Go to login
              </Link>
            </>
          ) : (
            <>
              {(validationError || resetMutation.error) && (
                <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
                  {validationError || resetMutation.error?.message}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">New password</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Min 8 characters"
                    className="block w-full px-3 py-2.5 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                    required
                    minLength={8}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1.5">Confirm password</label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder="Re-enter your password"
                    className="block w-full px-3 py-2.5 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                    required
                    minLength={8}
                  />
                </div>
                <button
                  type="submit"
                  disabled={resetMutation.isPending}
                  className="w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                >
                  {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
            </>
          )}

          <div className="mt-4 text-center">
            <Link
              to="/login"
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
            >
              Back to login
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-6">
          Halo Admin Panel &middot; Authorized personnel only
        </p>
      </div>
    </div>
  );
}
